// i18n runtime: derives the active locale from the URL, exposes t()/locale/localePath to the tree,
// and a locale-aware <T> Link that keeps navigation inside the current language.
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Link, type LinkProps, useLocation } from 'react-router-dom'
import { DEFAULT_LOCALE, type Locale, localeFromPath, localePath } from './config'
import { CATALOGS, loadExtraLocales } from './messages'

interface LocaleCtx {
  locale: Locale
  t: (key: string, vars?: Record<string, string | number>) => string
  /** Prefix a logical path with the active locale. */
  lp: (path: string) => string
}

const Ctx = createContext<LocaleCtx | null>(null)

function makeT(locale: Locale) {
  const cat = CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE] ?? {}
  const fallback = CATALOGS[DEFAULT_LOCALE] ?? {}
  return (key: string, vars?: Record<string, string | number>) => {
    let s = cat[key] ?? fallback[key] ?? key
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v))
    return s
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const locale = localeFromPath(pathname)
  // Base locales (en/de) are inline; the rest load their catalog lazily (data.extra.mjs is code-split).
  // Until it arrives, t() falls back to English; re-render once it's merged so native strings show.
  const [ready, setReady] = useState(() => !!CATALOGS[locale])
  useEffect(() => {
    if (CATALOGS[locale]) {
      setReady(true)
      return
    }
    let live = true
    setReady(false)
    loadExtraLocales().then(() => {
      if (live) setReady(true)
    })
    return () => {
      live = false
    }
  }, [locale])
  const value = useMemo<LocaleCtx>(
    () => ({ locale, t: makeT(locale), lp: (path: string) => localePath(locale, path) }),
    // biome-ignore lint/correctness/useExhaustiveDependencies: `ready` rebuilds t() once the lazy catalog lands
    [locale, ready]
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

function useLocaleCtx(): LocaleCtx {
  const ctx = useContext(Ctx)
  // Safe fallback (e.g. components rendered outside the provider in tests).
  if (!ctx) return { locale: DEFAULT_LOCALE, t: makeT(DEFAULT_LOCALE), lp: (p) => p }
  return ctx
}

export const useLocale = () => useLocaleCtx().locale
export const useT = () => useLocaleCtx().t
export const useLocalePath = () => useLocaleCtx().lp

/** Locale-aware <Link>: `to` is a logical path; it is auto-prefixed with the active locale. */
export function T({ to, ...rest }: Omit<LinkProps, 'to'> & { to: string }) {
  const lp = useLocalePath()
  return <Link to={lp(to)} {...rest} />
}

export {
  type Locale,
  LOCALES,
  DEFAULT_LOCALE,
  localePath,
  stripLocale,
  switchPath,
  localeFromPath,
  localeMeta,
  GATEWAYS,
} from './config'
